import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { soundManager } from '../utils/soundFeedback';

export const AdminConfigHub: React.FC = () => {
  const { language, t } = useLanguage();
  
  // Audio / Sound Feedback State
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(() => soundManager.isEnabled());

  // 1. Telegram State
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [telegramEnabled, setTelegramEnabled] = useState(true);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramMsg, setTelegramMsg] = useState('');
  const [telegramErr, setTelegramErr] = useState('');

  // 2. Cash Balances State
  const [cashBalances, setCashBalances] = useState<{ EUR: number; USD: number }>({ EUR: 0, USD: 0 });
  const [cashCurrency, setCashCurrency] = useState<'EUR' | 'USD'>('EUR');
  const [cashAmount, setCashAmount] = useState('');
  const [cashOpType, setCashOpType] = useState<'SET' | 'DEPOSIT' | 'WITHDRAW'>('SET');
  const [cashLoading, setCashLoading] = useState(false);
  const [cashMsg, setCashMsg] = useState('');

  // 3. Backups State
  const [backups, setBackups] = useState<any[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMsg, setBackupMsg] = useState('');

  // 4. Portfolio Target P/L Goal State
  const [goalType, setGoalType] = useState<'percent' | 'fixed'>('percent');
  const [goalValue, setGoalValue] = useState<string>('15');
  const [goalLoading, setGoalLoading] = useState<boolean>(false);
  const [goalMsg, setGoalMsg] = useState<string>('');
  const [goalErr, setGoalErr] = useState<string>('');

  const loadData = async () => {
    try {
      const [tgRes, cashRes, bkpRes, goalRes] = await Promise.all([
        adminApi.getTelegramSettings().catch(() => ({ data: {} })),
        adminApi.getCashBalances().catch(() => ({ data: { balances: { EUR: 0, USD: 0 } } })),
        adminApi.getBackups().catch(() => ({ data: { backups: [] } })),
        adminApi.getPortfolioGoal().catch(() => ({ data: { targetType: 'percent', targetValue: 15 } }))
      ]);

      if (tgRes.data) {
        setBotToken(tgRes.data.botToken || '');
        setChatId(tgRes.data.chatId || '');
        setTelegramEnabled(tgRes.data.isEnabled ?? true);
      }

      if (cashRes.data && cashRes.data.balances) {
        setCashBalances(cashRes.data.balances);
      }

      if (bkpRes.data && bkpRes.data.backups) {
        setBackups(bkpRes.data.backups);
      }

      if (goalRes.data) {
        setGoalType(goalRes.data.targetType || 'percent');
        setGoalValue(String(goalRes.data.targetValue || 15));
      }
    } catch (e) {
      console.error('Failed to load admin settings', e);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveTelegram = async (e: React.FormEvent) => {
    e.preventDefault();
    setTelegramLoading(true);
    setTelegramMsg('');
    setTelegramErr('');
    try {
      const res = await adminApi.updateTelegramSettings({
        botToken,
        chatId,
        isEnabled: telegramEnabled
      });
      setTelegramMsg(language === 'sk' ? 'Telegram konfigurácia bola úspešne uložená!' : 'Telegram settings saved successfully!');
    } catch (err: any) {
      setTelegramErr(err.response?.data?.error || 'Failed to save Telegram settings');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    setTelegramLoading(true);
    setTelegramMsg('');
    setTelegramErr('');
    try {
      const res = await adminApi.testTelegramAlert();
      setTelegramMsg(res.data?.message || 'Test sent!');
    } catch (err: any) {
      setTelegramErr(err.response?.data?.error || 'Test notification failed');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleUpdateCash = async (e: React.FormEvent) => {
    e.preventDefault();
    setCashLoading(true);
    setCashMsg('');
    try {
      await adminApi.updateCashBalance({
        currency: cashCurrency,
        amount: parseFloat(cashAmount) || 0,
        type: cashOpType
      });
      setCashMsg(language === 'sk' ? 'Hotovostný zostatok aktualizovaný!' : 'Cash balance updated successfully!');
      setCashAmount('');
      const cashRes = await adminApi.getCashBalances();
      if (cashRes.data?.balances) setCashBalances(cashRes.data.balances);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update cash balance');
    } finally {
      setCashLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setBackupLoading(true);
    setBackupMsg('');
    try {
      const res = await adminApi.createBackup();
      setBackupMsg(res.data?.message || 'Backup completed!');
      const bkpRes = await adminApi.getBackups();
      if (bkpRes.data?.backups) setBackups(bkpRes.data.backups);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to trigger backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSaveGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    setGoalLoading(true);
    setGoalMsg('');
    setGoalErr('');
    try {
      const val = parseFloat(goalValue);
      if (isNaN(val) || val <= 0) {
        setGoalErr(language === 'sk' ? 'Zadajte platné kladné číslo pre cieľ' : 'Please enter a valid positive number');
        return;
      }
      await adminApi.updatePortfolioGoal({
        targetType: goalType,
        targetValue: val
      });
      setGoalMsg(language === 'sk' ? 'Cieľ zhodnotenia portfólia bol úspešne uložený!' : 'Portfolio target goal saved successfully!');
    } catch (err: any) {
      setGoalErr(err.response?.data?.error || 'Failed to update goal');
    } finally {
      setGoalLoading(false);
    }
  };

  return (
    <div className="space-y-6 font-mono text-xs">
      {/* 0. AUDIO FEEDBACK & HAPTIC NOTIFICATION SWITCH */}
      <div className="glass-panel p-5 rounded-3xl bg-white border border-slate-200/90 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-lg text-indigo-700 shrink-0">
            {soundEnabled ? '🔔' : '🔕'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">
                {t("soundFeedbackTitle")}
              </h3>
              <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black border uppercase ${
                soundEnabled ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}>
                {soundEnabled ? (language === 'sk' ? 'ZAPNUTÉ' : 'ENABLED') : (language === 'sk' ? 'STÍŠENÉ' : 'MUTED')}
              </span>
            </div>
            <p className="text-[10px] text-stone-500 mt-0.5">
              {t("soundFeedbackDesc")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-center">
          <button
            type="button"
            onClick={() => {
              soundManager.playOrderChime();
            }}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-black text-[10px] transition-all border border-slate-200"
            title="Prehrať inštitucionálny ding"
          >
            🎵 {t("soundTestDing")}
          </button>

          <button
            type="button"
            onClick={() => {
              const next = !soundEnabled;
              soundManager.setEnabled(next);
              setSoundEnabledState(next);
              if (next) soundManager.playOrderChime();
            }}
            className={`px-4 py-1.5 rounded-xl font-black text-[11px] transition-all border shadow-xs ${
              soundEnabled
                ? 'bg-slate-900 text-white border-slate-900 hover:bg-black'
                : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-500'
            }`}
          >
            {soundEnabled ? (language === 'sk' ? '🔕 Stíšiť zvuky' : '🔕 Mute Sound') : (language === 'sk' ? '🔔 Aktivovať zvuky' : '🔔 Enable Sound')}
          </button>
        </div>
      </div>

      {/* 1. TELEGRAM PUSH NOTIFICATION WEBHOOK ENGINE */}
      <div className="glass-panel p-6 rounded-3xl bg-white border border-slate-200/90 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-200/80 pb-3">
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
              <span>✈️</span>
              <span>{language === 'sk' ? 'Telegram Bot & Real-time Webhook Notifikácie' : 'Telegram Bot & Real-time Webhook Hub'}</span>
            </h3>
            <p className="text-[10px] text-stone-500 mt-0.5">
              {language === 'sk' 
                ? 'Okamžité push notifikácie nových pokynov a prekročení cenových limitov do Telegramu s tlačidlom pre exekúciu' 
                : 'Instant order dispatches & price alert push notifications directly to trader Telegram channel'}
            </p>
          </div>
          <span className={`px-2.5 py-1 rounded-xl text-[10px] font-black border uppercase tracking-wider ${
            telegramEnabled && botToken 
              ? 'bg-emerald-50 text-emerald-700 border-emerald-300' 
              : 'bg-amber-50 text-amber-700 border-amber-300'
          }`}>
            {telegramEnabled && botToken ? (language === 'sk' ? '✓ AKTÍVNE' : '✓ ACTIVE') : (language === 'sk' ? 'POZASTAVENÉ' : 'DISABLED')}
          </span>
        </div>

        {telegramMsg && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-bold text-[11px]">
            {telegramMsg}
          </div>
        )}
        {telegramErr && (
          <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl font-bold text-[11px]">
            ⚠️ {telegramErr}
          </div>
        )}

        <form onSubmit={handleSaveTelegram} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase text-stone-600 mb-1">
                {language === 'sk' ? 'Telegram Bot Token (@BotFather)' : 'Telegram Bot Token'}
              </label>
              <input
                type="text"
                value={botToken}
                onChange={e => setBotToken(e.target.value)}
                placeholder="7123456789:AAH..."
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-mono text-xs focus:outline-none focus:border-indigo-600"
                required
              />
              <span className="text-[9px] text-stone-400 mt-1 block">
                {language === 'sk' ? 'Získate od @BotFather po príkaze /newbot' : 'Obtain from @BotFather via /newbot'}
              </span>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-stone-600 mb-1">
                {language === 'sk' ? 'Cieľové Chat ID / Kanál ID' : 'Target Chat ID / Channel ID'}
              </label>
              <input
                type="text"
                value={chatId}
                onChange={e => setChatId(e.target.value)}
                placeholder="-1001234567890 alebo vaše ID"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-mono text-xs focus:outline-none focus:border-indigo-600"
                required
              />
              <span className="text-[9px] text-stone-400 mt-1 block">
                {language === 'sk' ? 'ID vášho súkromného chatu alebo skupiny (zistíte napr. cez @userinfobot)' : 'User ID or Group ID (e.g. from @userinfobot)'}
              </span>
            </div>
          </div>

          {/* Granular Notification Subscriptions */}
          <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200/90 space-y-2.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">
              {language === 'sk' ? 'Typy odosielaných upozornení:' : 'Notification Event Types:'}
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 text-stone-800 font-bold cursor-pointer">
                <input
                  type="checkbox"
                  checked={telegramEnabled}
                  onChange={e => setTelegramEnabled(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-0"
                />
                <span>🚨 {t("tgOptionNewOrders")}</span>
              </label>

              <label className="flex items-center gap-2 text-stone-800 font-bold cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-0"
                />
                <span>⚡ {t("tgOptionExecuted")}</span>
              </label>

              <label className="flex items-center gap-2 text-stone-800 font-bold cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-0"
                />
                <span>🎯 {t("tgOptionPriceAlerts")}</span>
              </label>

              <label className="flex items-center gap-2 text-stone-800 font-bold cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-0"
                />
                <span>📈 {t("tgOptionMorningBrief")}</span>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 pt-2">
            <button
              type="submit"
              disabled={telegramLoading}
              className="px-5 py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl font-black transition-all shadow-sm"
            >
              {telegramLoading ? (language === 'sk' ? 'Ukladám...' : 'Saving...') : (language === 'sk' ? '💾 Uložiť Telegram Nastavenia' : '💾 Save Telegram Settings')}
            </button>

            <button
              type="button"
              onClick={handleTestTelegram}
              disabled={telegramLoading || !botToken || !chatId}
              className="px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-xl font-black transition-all disabled:opacity-50"
            >
              🚀 {language === 'sk' ? 'Odoslať Testovaciu Správu' : 'Send Test Notification'}
            </button>
          </div>
        </form>
      </div>

      {/* 2. MULTI-CURRENCY CASH BALANCE TRACKER (EUR & USD) */}
      <div className="glass-panel p-6 rounded-3xl bg-white border border-slate-200/90 shadow-sm space-y-4">
        <div className="border-b border-slate-200/80 pb-3">
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>💶</span>
            <span>{language === 'sk' ? 'Multi-Currency Peňažné Zostatky (Cash Hub)' : 'Multi-Currency Cash Reserves (Cash Hub)'}</span>
          </h3>
          <p className="text-[10px] text-stone-500 mt-0.5">
            {language === 'sk' 
              ? 'Správa likvidnej hotovosti v EUR a USD určenej na nákupy a evidenciu prijatých dividend' 
              : 'Track and manage free cash reserves in EUR and USD available for execution and dividends'}
          </p>
        </div>

        {cashMsg && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-bold text-[11px]">
            {cashMsg}
          </div>
        )}

        {/* Current Cash Balances KPI Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-emerald-50/50 border border-emerald-200/80 rounded-2xl flex flex-col justify-between">
            <span className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider">
              {language === 'sk' ? 'Voľná Hotovosť (EUR)' : 'Free Cash (EUR)'}
            </span>
            <span className="text-2xl font-black text-emerald-950 mt-1">
              €{Number(cashBalances.EUR || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="p-4 bg-sky-50/50 border border-sky-200/80 rounded-2xl flex flex-col justify-between">
            <span className="text-[10px] uppercase font-bold text-sky-800 tracking-wider">
              {language === 'sk' ? 'Voľná Hotovosť (USD)' : 'Free Cash (USD)'}
            </span>
            <span className="text-2xl font-black text-sky-950 mt-1">
              ${Number(cashBalances.USD || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Adjustment Form */}
        <form onSubmit={handleUpdateCash} className="bg-stone-50 p-4 rounded-2xl border border-stone-200 space-y-3">
          <span className="block text-[10px] font-extrabold uppercase text-stone-600">
            {language === 'sk' ? 'Upraviť alebo Zaúčtovať Pohyb Hotovosti' : 'Adjust or Book Cash Movement'}
          </span>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Mena' : 'Currency'}</label>
              <select
                value={cashCurrency}
                onChange={e => setCashCurrency(e.target.value as any)}
                className="w-full px-3 py-2 bg-white border border-stone-300 rounded-xl font-bold"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>

            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Operácia' : 'Operation'}</label>
              <select
                value={cashOpType}
                onChange={e => setCashOpType(e.target.value as any)}
                className="w-full px-3 py-2 bg-white border border-stone-300 rounded-xl font-bold"
              >
                <option value="SET">{language === 'sk' ? 'Nastaviť presnú sumu' : 'Set Exact Balance'}</option>
                <option value="DEPOSIT">{language === 'sk' ? 'Vklad / Dividenda (+)' : 'Deposit / Inflow (+)'}</option>
                <option value="WITHDRAW">{language === 'sk' ? 'Výber / Nákup (-)' : 'Withdraw / Outflow (-)'}</option>
              </select>
            </div>

            <div>
              <label className="block text-[9px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Suma' : 'Amount'}</label>
              <input
                type="number"
                step="any"
                required
                placeholder="0.00"
                value={cashAmount}
                onChange={e => setCashAmount(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-stone-300 rounded-xl font-bold text-xs"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={cashLoading || !cashAmount}
            className="w-full py-2.5 bg-stone-900 hover:bg-black text-white rounded-xl font-black transition-all disabled:opacity-50 text-xs"
          >
            {cashLoading ? (language === 'sk' ? 'Ukladám...' : 'Saving...') : (language === 'sk' ? 'Zaúčtovať do peňažného denníka' : 'Post to Cash Ledger')}
          </button>
        </form>
      </div>

      {/* 3. PORTFOLIO PERFORMANCE TARGET & BENCHMARK (TARGET P/L GOAL) */}
      <div className="glass-panel p-6 rounded-3xl bg-white border border-slate-200/90 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-200/80 pb-3">
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
              <span>🎯</span>
              <span>{language === 'sk' ? 'Cieľové Zhodnotenie Portfólia (Target P/L Goal)' : 'Portfolio Performance Target & Benchmark'}</span>
            </h3>
            <p className="text-[10px] text-stone-500 mt-0.5">
              {language === 'sk'
                ? 'Nastavte cieľové ročné zhodnotenie portfólia (p.a.) alebo fixnú sumu v EUR zobrazovanú v hlavnom paneli'
                : 'Configure annual ROI target percentage or fixed cash return displayed in the dashboard header'}
            </p>
          </div>
          <span className="px-2.5 py-1 rounded-xl text-[10px] font-black border uppercase tracking-wider bg-indigo-50 text-indigo-700 border-indigo-200">
            {goalType === 'percent' ? `+${goalValue}% p.a.` : `€${Number(goalValue || 0).toLocaleString()}`}
          </span>
        </div>

        {goalMsg && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-bold text-[11px] animate-[fadeIn_0.15s_ease-out]">
            ✓ {goalMsg}
          </div>
        )}
        {goalErr && (
          <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl font-bold text-[11px] animate-[fadeIn_0.15s_ease-out]">
            ⚠️ {goalErr}
          </div>
        )}

        <form onSubmit={handleSaveGoal} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Target Type Selector */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-stone-600 mb-1">
                {language === 'sk' ? 'Typ Cieľa' : 'Target Metric Type'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setGoalType('percent');
                    if (parseFloat(goalValue) > 100) setGoalValue('15');
                  }}
                  className={`p-2.5 rounded-xl border text-center transition-all font-bold text-xs flex items-center justify-center gap-1.5 ${
                    goalType === 'percent'
                      ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span>📈</span>
                  <span>{language === 'sk' ? '% Ročný výnos (p.a.)' : '% Annual ROI'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGoalType('fixed');
                    if (parseFloat(goalValue) <= 100) setGoalValue('30000');
                  }}
                  className={`p-2.5 rounded-xl border text-center transition-all font-bold text-xs flex items-center justify-center gap-1.5 ${
                    goalType === 'fixed'
                      ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span>💶</span>
                  <span>{language === 'sk' ? 'Fixná suma v EUR' : 'Fixed EUR Amount'}</span>
                </button>
              </div>
            </div>

            {/* Target Value Input */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-stone-600 mb-1">
                {goalType === 'percent' 
                  ? (language === 'sk' ? 'Cieľové zhodnotenie (%)' : 'Target Return (%)')
                  : (language === 'sk' ? 'Cieľový čistý zisk (€)' : 'Target Net Profit (€)')}
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  min="0.1"
                  value={goalValue}
                  onChange={e => setGoalValue(e.target.value)}
                  placeholder={goalType === 'percent' ? '15' : '30000'}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-mono text-sm font-black focus:outline-none focus:border-indigo-600 focus:bg-white"
                  required
                />
                <span className="absolute right-3 top-2 text-xs font-black text-slate-400">
                  {goalType === 'percent' ? '% p.a.' : 'EUR'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Presets for Percent */}
          {goalType === 'percent' && (
            <div>
              <span className="block text-[9px] uppercase font-bold text-stone-500 mb-1.5">
                {language === 'sk' ? 'Rýchla voľba benchmarku:' : 'Quick benchmark presets:'}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { val: '8', label: '+8% (Konzervatívny)' },
                  { val: '10', label: '+10% (Index S&P)' },
                  { val: '12', label: '+12% (Vyvážený)' },
                  { val: '15', label: '+15% (Inštitucionálny)' },
                  { val: '20', label: '+20% (Rastový)' },
                  { val: '25', label: '+25% (Dynamický)' },
                  { val: '30', label: '+30% (Agresívny)' }
                ].map(p => (
                  <button
                    key={p.val}
                    type="button"
                    onClick={() => setGoalValue(p.val)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold transition-all border ${
                      goalValue === p.val
                        ? 'bg-indigo-600 text-white border-indigo-700 shadow-2xs'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                    }`}
                  >
                    {language === 'sk' ? p.label : `+${p.val}%`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 bg-slate-50 border border-slate-200/90 rounded-2xl flex items-start gap-2.5 text-[11px] text-slate-600">
            <span className="text-base shrink-0">💡</span>
            <div>
              <strong className="text-slate-900 block mb-0.5">
                {language === 'sk' ? 'Ako funguje výpočet cieľa?' : 'How is this target calculated?'}
              </strong>
              {goalType === 'percent' ? (
                <span>
                  {language === 'sk'
                    ? `Cieľový P/L sa počíta ako Investované prostriedky × ${goalValue || 15}%. Percento plnenia (odznak na Dashboarde) ukazuje pomer medzi aktuálnym ziskom a týmto cieľom.`
                    : `Target P/L is computed as Invested Capital × ${goalValue || 15}%. The progress badge on Dashboard measures actual yield against this benchmark.`}
                </span>
              ) : (
                <span>
                  {language === 'sk'
                    ? `Cieľový čistý zisk je nastavený na fixnú sumu €${Number(goalValue || 0).toLocaleString()}. Po dosiahnutí tejto hodnoty zisku bude cieľ splnený na 100%.`
                    : `Target net profit is set to a fixed €${Number(goalValue || 0).toLocaleString()}. Reaching this net gain represents 100% progress.`}
                </span>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={goalLoading || !goalValue}
            className="w-full py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl font-black transition-all disabled:opacity-50 text-xs shadow-sm"
          >
            {goalLoading ? (language === 'sk' ? 'Ukladám...' : 'Saving...') : (language === 'sk' ? 'Uložiť Cieľové Zhodnotenie' : 'Save Performance Goal')}
          </button>
        </form>
      </div>

      {/* 4. POSTGRESQL AUTOMATIC BACKUPS & SNAPSHOTS */}
      <div className="glass-panel p-6 rounded-3xl bg-white border border-slate-200/90 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-200/80 pb-3">
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
              <span>💾</span>
              <span>{language === 'sk' ? 'Automatické Zálohy & Snapshoty Databázy' : 'Automated Database Backups & Snapshots'}</span>
            </h3>
            <p className="text-[10px] text-stone-500 mt-0.5">
              {language === 'sk' 
                ? 'História vytvorených SQL snapshotov pred importmi výpisov a možnosť manuálneho vytvorenia zálohy' 
                : 'Full SQL database backup snapshots created before statement syncs'}
            </p>
          </div>
          <button
            onClick={handleCreateBackup}
            disabled={backupLoading}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black transition-all shadow-sm flex items-center gap-1.5 text-xs"
          >
            <span>⚡</span>
            <span>{backupLoading ? (language === 'sk' ? 'Vytváram...' : 'Backing up...') : (language === 'sk' ? 'Vytvoriť Zálohu Teraz' : 'Trigger Backup Now')}</span>
          </button>
        </div>

        {backupMsg && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-bold text-[11px]">
            {backupMsg}
          </div>
        )}

        <div className="overflow-x-auto border border-stone-200 rounded-xl">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-[10px] uppercase font-extrabold text-stone-600">
                <th className="p-2.5">{language === 'sk' ? 'Súbor zálohy' : 'Snapshot Archive'}</th>
                <th className="p-2.5 text-right">{language === 'sk' ? 'Veľkosť' : 'Size'}</th>
                <th className="p-2.5 text-center">{language === 'sk' ? 'Stav' : 'Status'}</th>
                <th className="p-2.5 text-right">{language === 'sk' ? 'Čas vytvorenia' : 'Timestamp'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {backups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-stone-400">
                    {language === 'sk' ? 'Žiadne archivované zálohy' : 'No backup snapshots found'}
                  </td>
                </tr>
              ) : (
                backups.map(b => (
                  <tr key={b.id} className="hover:bg-stone-50/50">
                    <td className="p-2.5 font-bold text-stone-850 flex items-center gap-1.5">
                      <span>📦</span>
                      <span className="truncate max-w-[240px]">{b.filename}</span>
                    </td>
                    <td className="p-2.5 text-right text-stone-600">
                      {(Number(b.size_bytes) / (1024 * 1024)).toFixed(2)} MB
                    </td>
                    <td className="p-2.5 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {b.status || 'COMPLETED'}
                      </span>
                    </td>
                    <td className="p-2.5 text-right text-stone-500 text-[10px]">
                      {new Date(b.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
